import React from "react";
import { Stack, Box, InputAdornment } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import PercentIcon from "@mui/icons-material/Percent";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch } from "../redux/store";
import { createTaxCode, updateTaxCode } from "../redux/actions/taxCodeActions";
import { cleanFormData } from "../helpers/formHelpers";
import { taxCodeActions } from "../redux/slices/taxCode";

import { TaxCodeRequestDto } from "../shared/dtos/taxCode/TaxCodeRequestDto";
import { TaxCodeResponseDto } from "../shared/dtos/taxCode/TaxCodeResponseDto";

const taxCodeSchema = Yup.object().shape({
  code: Yup.string().required("Tax code is required").max(10),
  name: Yup.string().required("Display name is required"),
  taxRate: Yup.number().required("Rate is required").min(0).max(100),
});

interface CreateOrUpdateTaxCodeFormProps {
  taxCode?: TaxCodeResponseDto | null;
  isEditing?: boolean;
  loading: boolean;
  error: string | null;
}

const CreateOrUpdateTaxCodeForm: React.FC<CreateOrUpdateTaxCodeFormProps> = ({
  taxCode = null,
  isEditing = false,
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();

  const initialValues: Partial<TaxCodeRequestDto> = {
    code: taxCode?.code || "",
    name: taxCode?.name || "",
    taxRate: taxCode?.taxRate || ("" as unknown as number),
  };

  return (
    <Box sx={{ width: "100%" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={() =>
          dispatch(
            isEditing
              ? taxCodeActions.setErrorUpdate(null)
              : taxCodeActions.setErrorCreate(null),
          )
        }
      />
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={taxCodeSchema}
        onSubmit={(values) => {
          const payload = cleanFormData(values) as unknown as TaxCodeRequestDto;
          if (isEditing && taxCode) {
            dispatch(updateTaxCode({ id: taxCode.id, values: payload }));
          } else {
            dispatch(createTaxCode(payload));
          }
        }}
      >
        {({ dirty, isValid }: FormikProps<Partial<TaxCodeRequestDto>>) => (
          <Form>
            <Stack spacing={3}>
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 2fr" },
                }}
              >
                <FormTextField
                  name="code"
                  label="Tax Code"
                  placeholder="e.g., B"
                />
                <FormTextField
                  name="name"
                  label="Tax Name"
                  placeholder="e.g., 16% VAT"
                />
              </Box>

              <FormTextField
                name="taxRate"
                label="Tax Rate (%)"
                type="number"
                placeholder="e.g., 16.00"
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <PercentIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  },
                }}
              />

              <SubmitButton
                title={isEditing ? "Save Changes" : "Create Tax Code"}
                loading={loading}
                disabled={!dirty || !isValid || loading}
                startIcon={
                  isEditing ? <SaveOutlinedIcon /> : <AddCircleOutlineIcon />
                }
                fullWidth
                sx={{ py: 1.5, fontWeight: 700 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default CreateOrUpdateTaxCodeForm;
